import { ErrorTypes, Eva2Error } from '~/lib/errors/Eva2Error';

export type SingleStepContext = {
  type: string;
  urlGetConfig?: string;
  config?: FormField[];
  urlPost: string;
  anonym: boolean;
  forceRefreshProfileOnComplete: boolean;
  checkValidatedFormOnMounted: boolean;
  cancelable: boolean;
  labelSubmit?: string;
};

export const getFormSingleStepContextWithConfigData = (
  config: FormField[],
  urlPost: string,
  anonym: boolean,
  forceRefreshProfileOnComplete: boolean = true,
  checkValidatedFormOnMounted: boolean = false,
  cancelable: boolean = true,
  labelSubmit: string = 'Valider'
): SingleStepContext => {
  return {
    type: FormStrategyType.SingleStep,
    config,
    urlPost,
    anonym,
    forceRefreshProfileOnComplete,
    checkValidatedFormOnMounted,
    cancelable,
    labelSubmit,
  };
};

export const getFormSingleStepContextWithUrlConfig = (
  urlGetConfig: string,
  urlPost: string,
  anonym: boolean,
  forceRefreshProfileOnComplete: boolean = true,
  checkValidatedFormOnMounted: boolean = false,
  cancelable: boolean = true,
  labelSubmit: string = 'Valider'
): SingleStepContext => {
  return {
    type: FormStrategyType.SingleStep,
    urlGetConfig,
    urlPost,
    anonym,
    forceRefreshProfileOnComplete,
    checkValidatedFormOnMounted,
    cancelable,
    labelSubmit,
  };
};

export const useFormStrategySingleStep = (context: SingleStepContext) => {
  const data = ref<any>();

  const updated = ref<boolean>(false);

  const error = ref<Eva2Error>(null);

  const completed = ref<boolean>(false);

  const count = computed(() => 1);

  const index = computed(() => 0);

  const hasPrevious = computed(() => false);

  const hasCancel = computed(() => context.cancelable);

  const loading = ref<boolean>(false);

  const submitEvent = createEventHook<any>();

  const formNavigation = useFormNavigationObserver(context.labelSubmit);

  const profileLoadFromNetwork = useProfileLoadFromNetwork();

  const checkValidatedFormOnMounted = computed(
    () => context.checkValidatedFormOnMounted
  );

  if (context.config) {
    data.value = context.config;
    loading.value = false;
  }

  const step = computed(() => {
    if (!data.value) return;
    return {
      id: 'single',
      fields: data.value,
    };
  });

  const fields = computed(() => {
    return data.value ?? [];
  });

  const title = computed(() => {
    return '';
  });

  const fetch = async () => {
    if (data.value) return;
    loading.value = true;
    const { result, error: errorRequest } = await useApiFetch(
      context.urlGetConfig,
      null,
      context.anonym
    );
    if (errorRequest) {
      error.value = new Eva2Error(
        ErrorTypes.Adam,
        {},
        errorRequest.response?._data?.summary || "une erreur s'est produite"
      );
    } else {
      data.value = result;
    }

    loading.value = false;
  };

  const getSubmitBody = () => {
    const props = {
      ...toRaw(formFieldsData.memberValues.value),
    };
    const keyValues = {};
    Object.keys(props).forEach((prop) => {
      keyValues[prop] = props[prop].value;
    });
    return keyValues;
  };

  const submit = async () => {
    if (loading.value) return;
    loading.value = true;
    const request = await useApiFetch(
      context.urlPost,
      {
        method: 'POST',
        body: getSubmitBody(),
      },
      context.anonym
    );

    const { error: errorRequest } = request;
    if (!errorRequest) {
      submitEvent.trigger(request);
      if (context.forceRefreshProfileOnComplete) {
        await profileLoadFromNetwork.run();
        await useUserLoadFromNetwork();
      }
      loading.value = false;
      completed.value = true;
    } else {
      loading.value = false;
      error.value = new Eva2Error(
        ErrorTypes.Adam,
        {},
        errorRequest.response?._data?.summary || "une erreur s'est produite"
      );
    }
  };

  const releaseError = () => {
    error.value = null;
  };

  const gotoStep = async (stepId) => {};

  const previous = async () => {
    return null;
  };

  const formFieldsData = useFormFieldsData(step, true);

  const formFieldsValidation = useFormFieldsValidation(
    fields,
    formFieldsData.memberValues
  );

  const ready = computed(() => {
    if (!step.value) return false;
    return step.value?.id === formFieldsData.id.value;
  });

  const formFieldsRecap = useFormFieldsRecap(step);

  const { hasFieldsReaquired } = useFormFieldsIsRequired(fields);

  return {
    context,
    fetch,
    submit,
    previous,
    gotoStep,
    step,
    index,
    count,
    title,
    fields,
    hasPrevious,
    hasCancel,
    formFieldsData,
    formFieldsValidation,
    ready,
    completed,
    updated,
    formFieldsRecap,
    loading,
    hasFieldsReaquired,
    onSubmitComplete: submitEvent.on,
    checkValidatedFormOnMounted,
    getSubmitBody,
    formNavigation,
    releaseError,
    error,
  };
};
